import { z } from 'zod'
import { timestampSchema, uuidSchema } from './common.js'

/**
 * Freigabe-Workflow fuer kuratierte KB-Dokumente (RNA M5).
 *
 * Der Retrieval-Filter aus M2 haelt alles zurueck, was nicht `freigegeben`
 * ist. Diese API ist der einzige Weg, das zu aendern, und sie kennt bewusst
 * nur den Einzelfall: `path` ist ein String, nie eine Liste. Eine Freigabe ist
 * eine Personenhandlung, und eine Person entscheidet ueber ein Dokument.
 */

export const freigabeStatusSchema = z.enum([
  'entwurf',
  'in_pruefung',
  'freigegeben',
  'zurueckgezogen',
])
export type FreigabeStatusValue = z.infer<typeof freigabeStatusSchema>

/** Der Entscheid einer Fachperson — die drei Aktionen der Freigabe-Ansicht. */
export const freigabeEntscheidSchema = z.enum([
  'freigegeben',
  'zurueckgewiesen',
  'ueberarbeitung',
])
export type FreigabeEntscheidValue = z.infer<typeof freigabeEntscheidSchema>

/** Ein Dokument in der Freigabe-Liste. */
export const kbFreigabeDokumentSchema = z.object({
  path: z.string(),
  titel: z.string(),
  doc_type: z.string().nullable(),
  status: freigabeStatusSchema,
  /** Wer zuletzt entschieden hat — null, solange niemand geprueft hat. */
  durch: z.string().nullable(),
  datum: z.string().nullable(),
  entscheid: freigabeEntscheidSchema.nullable(),
  begruendung: z.string().nullable(),
  /** Aenderungsdatum der Datei — die Sortiergroesse der Liste. */
  geaendert_am: timestampSchema,
  chunks_count: z.number().int().nonnegative(),
})
export type KbFreigabeDokument = z.infer<typeof kbFreigabeDokumentSchema>

/** Ein gerenderter Chunk in der Detailansicht. */
export const kbFreigabeChunkSchema = z.object({
  chunk_index: z.number().int().nonnegative(),
  section_path: z.string(),
  contextual_prefix: z.string(),
  chunk_text: z.string(),
  contains_table: z.boolean(),
  token_count: z.number().int().nonnegative(),
})
export type KbFreigabeChunk = z.infer<typeof kbFreigabeChunkSchema>

/**
 * Die Quellenlage des Dokuments, so wie das Retrieval sie sehen wuerde.
 * `zitatlabel` ist genau das Label, das eine Antwort tragen wuerde — die
 * pruefende Person sieht damit die Zitierform, nicht nur die Rohfelder.
 */
export const kbFreigabeQuellenSchema = z.object({
  zitatlabel: z.string(),
  quellenlage: z.string().nullable(),
  tier: z.number().int().nullable(),
  rechtsraum: z.string().nullable(),
  normfamilie: z.string().nullable(),
  ziffer: z.string().nullable(),
  belastbarkeit: z.string().nullable(),
  ocr_qualitaet: z.string().nullable(),
  stand_pruefung: z.string().nullable(),
  gueltig_von: z.string().nullable(),
  gueltig_bis: z.string().nullable(),
  /** Wikilinks des Dokuments, in der Regel auf `QS_`-Quelle-Sidecars. */
  verweise: z.array(z.string()),
})
export type KbFreigabeQuellen = z.infer<typeof kbFreigabeQuellenSchema>

/** Ein unveraenderlicher Eintrag aus dem Audit-Log zu diesem Dokument. */
export const kbFreigabeAuditEintragSchema = z.object({
  id: uuidSchema,
  entscheid: freigabeEntscheidSchema.nullable(),
  status_vorher: freigabeStatusSchema.nullable(),
  status_nachher: freigabeStatusSchema.nullable(),
  durch: z.string().nullable(),
  actor_user_id: uuidSchema.nullable(),
  begruendung: z.string().nullable(),
  created_at: timestampSchema,
})
export type KbFreigabeAuditEintrag = z.infer<typeof kbFreigabeAuditEintragSchema>

export const kbFreigabeListResponseSchema = z.object({
  dokumente: z.array(kbFreigabeDokumentSchema),
})
export type KbFreigabeListResponse = z.infer<typeof kbFreigabeListResponseSchema>

export const kbFreigabeDetailResponseSchema = z.object({
  dokument: kbFreigabeDokumentSchema,
  chunks: z.array(kbFreigabeChunkSchema),
  quellen: kbFreigabeQuellenSchema,
  verlauf: z.array(kbFreigabeAuditEintragSchema),
})
export type KbFreigabeDetailResponse = z.infer<typeof kbFreigabeDetailResponseSchema>

/**
 * POST /api/kb/freigaben/entscheid — ein Dokument, ein Entscheid.
 *
 * `strict()` ist hier kein Stilmittel: es laesst einen Aufruf mit `paths: [...]`
 * hart auflaufen, statt das Feld still zu ignorieren und das erste Dokument zu
 * entscheiden. Es gibt keine Stapelvariante dieses Endpunkts.
 *
 * `durch` fehlt bewusst — die entscheidende Person kommt aus der Sitzung, nicht
 * aus dem Request. Sonst koennte eine Freigabe einer Person zugeschrieben
 * werden, die das Dokument nie gesehen hat.
 */
export const kbFreigabeEntscheidRequestSchema = z
  .object({
    path: z.string().min(1).max(1024),
    entscheid: freigabeEntscheidSchema,
    begruendung: z.string().max(4000).optional(),
  })
  .strict()
  .refine(
    (value) =>
      value.entscheid === 'freigegeben' || (value.begruendung?.trim().length ?? 0) > 0,
    {
      message: 'Zurueckweisung und Ueberarbeitung brauchen eine Begruendung',
      path: ['begruendung'],
    },
  )
export type KbFreigabeEntscheidRequest = z.infer<typeof kbFreigabeEntscheidRequestSchema>

export const kbFreigabeEntscheidResponseSchema = z.object({
  dokument: kbFreigabeDokumentSchema,
  /**
   * Ergebnis des Re-Ingest. Der Statuswechsel aendert den `source_hash`, damit
   * die Chunks den neuen Status uebernehmen. Schlaegt der Ingest fehl, bleiben
   * die Chunks auf dem alten Stand — fail-closed, aber sichtbar zu machen.
   */
  reindex: z.object({
    outcome: z.string(),
    message: z.string(),
    chunks: z.number().int().nonnegative(),
  }),
})
export type KbFreigabeEntscheidResponse = z.infer<typeof kbFreigabeEntscheidResponseSchema>
